import React, { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';

import { Swiper, SwiperSlide } from 'swiper/react';

import { LightTooltip } from '~/components/UI/LightTooltip/LightTooltip';
import { ICaseStudyWithProgress } from '~/hooks/useCaseStudies';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import CaseStudyCard from '~/pages/SingleVideoPage/CaseStudyCard';
import TestCard from '~/pages/SingleVideoPage/TestCard';
import VideoCard from '~/pages/SingleVideoPage/VideoCard';
import { PATHWAY_ITEM_TYPES, VideoStatus } from '~/pages/const';
import { buildCaseStudyRoute, buildPathwayTestRoute } from '~/routes';
import { BREAKPOINTS_VALUES, LARGE_MARGIN, SMALL_MARGIN } from '~/theme';
import { CaseStudy, Maybe, Pathway, Quiz, Video } from '~/types';

type RenderCaseStudyCard = ({
  caseStudy,
  pathway,
}: {
  caseStudy: ICaseStudyWithProgress;
  pathway: Maybe<Pathway>;
  isActive?: boolean;
}) => ReactElement;
type RenderTestCard = ({
  quiz,
  canPass,
  pathway,
}: {
  quiz: Maybe<Quiz>;
  canPass: boolean;
  pathway: Maybe<Pathway>;
}) => ReactElement;
type RenderVideoCard = ({
  video,
  pathway,
  isActive,
  link,
  status,
}: {
  video: Maybe<Video>;
  pathway: Maybe<Pathway>;
  isActive: boolean;
  link: string;
  status: VideoStatus;
}) => ReactNode;

interface IListOfVideos {
  breakpoints?: typeof defaultBreakpoints;
  spaceBetween?: number;
  pathwayItems: (CaseStudy | Video)[] | Maybe<Video>[];
  nextItemId?: Maybe<string>;
  nextItemType?: PATHWAY_ITEM_TYPES;
  availableQuizes?: Maybe<Quiz>[];
  quizes?: Maybe<Quiz>[] | null;
  progressData: any;
  pathway: Maybe<Pathway>;
  handleClickOnActive?: () => void;
  renderVideoCard?: RenderVideoCard;
  renderTestCard?: RenderTestCard;
  renderCaseStudyCard?: RenderCaseStudyCard;
}

const defaultBreakpoints = {
  [BREAKPOINTS_VALUES.lg]: {
    slidesPerView: 8.5,
  },
  [BREAKPOINTS_VALUES.md]: {
    slidesPerView: 5.5,
  },
  [BREAKPOINTS_VALUES.sm]: {
    slidesPerView: 3.5,
  },
};

const PathwayItems = ({
  breakpoints = defaultBreakpoints,
  spaceBetween = SMALL_MARGIN,
  pathwayItems,
  quizes,
  availableQuizes,
  progressData,
  nextItemId,
  nextItemType,
  pathway,
  handleClickOnActive,
  renderVideoCard = ({ video, pathway, isActive, link, status }) => (
    <VideoCard
      title={video?.title}
      preview={video?.preview_url}
      isActive={isActive}
      status={status}
      link={link}
      pathwaySlug={pathway?.slug}
    />
  ),
  renderTestCard = ({ quiz, canPass, pathway }) => (
    <TestCard
      id={`quizCard${pathway?.id}`}
      caption={quiz?.title}
      link={buildPathwayTestRoute(pathway?.slug, quiz?.id)}
      canPass={canPass}
    />
  ),
  renderCaseStudyCard = ({ pathway, caseStudy }) => (
    <CaseStudyCard
      link={buildCaseStudyRoute(pathway?.slug, caseStudy?.id)}
      imageUrl={caseStudy?.image_url}
      title={caseStudy?.title}
      passed={caseStudy.passed}
    />
  ),
}: IListOfVideos) => {
  const activeVideoIndex = pathwayItems.findIndex(
    (item) => item?.id === nextItemId && item?.__typename === nextItemType,
  );
  const [runnedFix, setRunnedFix] = useState(false);
  const swiperRef = useRef<any>(null);
  const { t } = useTenantTranslation();

  //fix issue with useless slide if initialSlide < slidesPerView
  useEffect(() => {
    if (!runnedFix && swiperRef?.current) {
      if (Math.floor(swiperRef?.current?.params?.slidesPerView) > activeVideoIndex) {
        swiperRef?.current?.slideTo(0, 0);
      }
      setRunnedFix(true);
    }
  }, [runnedFix, activeVideoIndex]);

  return (
    <Swiper
      slidesPerView={2.5}
      spaceBetween={spaceBetween}
      breakpoints={breakpoints}
      navigation
      slidesOffsetAfter={LARGE_MARGIN}
      initialSlide={activeVideoIndex}
      onSwiper={(swiper) => (swiperRef.current = swiper)}
    >
      {pathwayItems?.map((item, index) => {
        const isActive = nextItemId === item?.id && nextItemType === item?.__typename;
        if (item?.__typename === PATHWAY_ITEM_TYPES.CaseStudy) {
          return (
            <SwiperSlide key={index}>
              {renderCaseStudyCard({ pathway, caseStudy: item, isActive })}
            </SwiperSlide>
          );
        }

        if (item?.__typename === PATHWAY_ITEM_TYPES.Video) {
          const videoProgress = item?.id && progressData?.[item?.id];
          const notWatched =
            (!videoProgress || videoProgress?.progress === 0) && VideoStatus.NotWatched;
          const completed = videoProgress?.completed && VideoStatus.Completed;
          const watched =
            videoProgress?.progress > 0 && videoProgress?.progress < 100 && VideoStatus.Watched;
          const status = completed || notWatched || watched;

          const link = videoProgress ? videoProgress.link : `/videos/${item?.video_url}`;

          return (
            <SwiperSlide
              key={index}
              data-testid="swiperSlideVideoCard"
              onClick={() => isActive && handleClickOnActive?.()}
            >
              {renderVideoCard({ video: item, pathway, isActive, link, status })}
            </SwiperSlide>
          );
        }

        return null;
      })}
      {quizes?.map((quiz) => {
        const canPass = !!availableQuizes?.find((item) => item?.id === quiz?.id);
        return (
          <SwiperSlide key={quiz?.id}>
            <LightTooltip
              placement="top"
              title={t('page.pathway.completeAllDesc')}
              disableHoverListener={canPass}
            >
              {renderTestCard({ quiz, canPass, pathway })}
            </LightTooltip>
          </SwiperSlide>
        );
      })}
    </Swiper>
  );
};

export default PathwayItems;
